import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ListPagination from "@/components/ListPagination";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogScrollBody } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users as UsersIcon, Server, Plus, Trash2, Shield, Send, Mail, Activity, Pencil, UserCheck, UserX, CreditCard, ListTodo, RefreshCw, RotateCcw, AlertTriangle, Lock, ShieldCheck, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";

const DELIVERY_TYPES = ["smtp", "sendgrid", "resend", "mock"];

const EMPTY_ADMIN_STATS = {
  total_users: 0,
  total_audiences: 0,
  total_contacts: 0,
  total_campaigns: 0,
  sent_campaigns: 0,
  total_recipients: 0,
  total_opens: 0,
  total_clicks: 0,
  active_servers: 0,
  open_rate: 0,
};

const TYPE_FIELDS = {
  smtp: [
    { key: "host", label: "SMTP host", placeholder: "smtp.yourdomain.com" },
    { key: "port", label: "Port", placeholder: "587 (STARTTLS) / 465 (SSL)", type: "number" },
    { key: "username", label: "Username" },
    { key: "password", label: "Password", type: "password" },
    { key: "security", label: "Encryption", placeholder: "auto | starttls | ssl | none" },
  ],
  sendgrid: [{ key: "api_key", label: "SendGrid API key", type: "password", placeholder: "SG.xxxxxxxx" }],
  resend: [{ key: "api_key", label: "Resend API key", type: "password", placeholder: "re_xxxxxxxx" }],
  mock: [],
};

const IMAP_SECURITY_OPTIONS = [
  { value: "auto", label: "Auto (993→SSL, 143→STARTTLS)" },
  { value: "ssl", label: "SSL / TLS (port 993)" },
  { value: "starttls", label: "STARTTLS (port 143)" },
  { value: "none", label: "None (plain — not recommended)" },
];

const BOUNCE_INBOX_FIELDS = [
  { key: "email", label: "Bounce email address", placeholder: "bounces@yourdomain.com" },
  { key: "imap_host", label: "IMAP host", placeholder: "mail.yourdomain.com" },
  { key: "imap_port", label: "IMAP port", placeholder: "993 or 143", type: "number" },
  { key: "imap_security", label: "Encryption", type: "select" },
  { key: "imap_username", label: "IMAP username (defaults to bounce email)" },
  { key: "imap_password", label: "IMAP password", type: "password" },
  { key: "imap_folder", label: "IMAP folder", placeholder: "INBOX or INBOX,INBOX.Delivery Reports" },
];

const FBL_INBOX_FIELDS = [
  { key: "email", label: "FBL / abuse mailbox", placeholder: "abuse@yourdomain.com" },
  { key: "imap_host", label: "IMAP host", placeholder: "mail.yourdomain.com" },
  { key: "imap_port", label: "IMAP port", placeholder: "993 or 143", type: "number" },
  { key: "imap_security", label: "Encryption", type: "select" },
  { key: "imap_username", label: "IMAP username (defaults to FBL email)" },
  { key: "imap_password", label: "IMAP password", type: "password" },
  { key: "imap_folder", label: "IMAP folder", placeholder: "INBOX or INBOX,INBOX.Delivery Reports" },
];

const renderInboxField = (f, form, setForm) => {
  if (f.type === "select" && f.key === "imap_security") {
    return (
      <div key={f.key}>
        <Label className="text-xs font-bold uppercase tracking-wider">{f.label}</Label>
        <Select
          value={form.config.imap_security || "auto"}
          onValueChange={(v) => setForm({ ...form, config: { ...form.config, imap_security: v } })}
        >
          <SelectTrigger className="mt-2 rounded-sm" data-testid={`server-config-${f.key}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {IMAP_SECURITY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div key={f.key}>
      <Label className="text-xs font-bold uppercase tracking-wider">{f.label}</Label>
      <Input
        type={f.type === "select" ? "text" : f.type || "text"}
        placeholder={f.placeholder}
        value={form.config[f.key] || ""}
        onChange={(e) => setForm({ ...form, config: { ...form.config, [f.key]: e.target.value } })}
        className="mt-2 rounded-sm"
        data-testid={`server-config-${f.key}`}
      />
    </div>
  );
};

const RATE_FIELDS = [
  { key: "max_per_hour", label: "Max emails / hour", placeholder: "0 = unlimited", type: "number" },
  { key: "max_per_day", label: "Max emails / day", placeholder: "0 = unlimited", type: "number" },
];

const isDeliveryServer = (s) => DELIVERY_TYPES.includes(s.type);
const isInboxServer = (s) => s.type === "bounce" || s.type === "fbl";

const ServerConfigSummary = ({ config, server }) => {
  const hidden = new Set(["bounce_server_id", "fbl_server_id", "password", "api_key", "imap_password"]);
  const entries = Object.entries(config || {}).filter(([k]) => !hidden.has(k));
  return (
    <div className="text-xs text-neutral-500 dark:text-neutral-400 space-y-1 mb-4">
      {server.bounce_server_name && (
        <div><span className="font-bold">Bounce server:</span> {server.bounce_server_name}</div>
      )}
      {server.fbl_server_name && (
        <div><span className="font-bold">FBL server:</span> {server.fbl_server_name}</div>
      )}
      {entries.map(([k, v]) => (
        <div key={k}><span className="font-bold">{k}:</span> {String(v)}</div>
      ))}
    </div>
  );
};

const Stat = ({ label, value, Icon }) => (
  <div className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm">
    <div className="flex items-center justify-between mb-6">
      <span className="label-overline">{label}</span>
      <Icon className="h-4 w-4 text-neutral-400" />
    </div>
    <div className="font-display font-black tracking-tighter text-4xl">{value.toLocaleString()}</div>
  </div>
);

export default function AdminPage() {
  const confirm = useConfirm();
  const [stats, setStats] = useState(null);
  const [servers, setServers] = useState([]);
  const [metaLoading, setMetaLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", type: "mock", config: {}, is_active: true });
  const [testOpen, setTestOpen] = useState(false);
  const [testServer, setTestServer] = useState(null);
  const [testForm, setTestForm] = useState({ to_email: "", from_email: "", from_name: "Mailwave Test" });
  const [testingSend, setTestingSend] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [pollingId, setPollingId] = useState(null);
  const [testingImapId, setTestingImapId] = useState(null);
  const [testingInboxForm, setTestingInboxForm] = useState(false);
  const [inboxTestOpen, setInboxTestOpen] = useState(false);
  const [inboxTestResult, setInboxTestResult] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planEditOpen, setPlanEditOpen] = useState(false);
  const [planEditingId, setPlanEditingId] = useState(null);
  const [planEditorForm, setPlanEditorForm] = useState({
    id: "",
    name: "",
    price: "0",
    max_contacts: "1000",
    max_emails_per_month: "5000",
    features: "",
    is_active: true,
    sort_order: "0",
  });
  const [savingPlan, setSavingPlan] = useState(false);
  const [userCreateOpen, setUserCreateOpen] = useState(false);
  const [userForm, setUserForm] = useState({ email: "", password: "", name: "", company: "", is_admin: false, plan: "free", email_credits: "", organization_id: "" });
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgCreateForm, setOrgCreateForm] = useState({ name: "", default_tracking_domain: "" });
  const [orgMembersOpen, setOrgMembersOpen] = useState(false);
  const [orgMembersTarget, setOrgMembersTarget] = useState(null);
  const [orgMemberIds, setOrgMemberIds] = useState([]);
  const [allUsersForOrg, setAllUsersForOrg] = useState([]);
  const [savingOrgMembers, setSavingOrgMembers] = useState(false);
  const [manageUser, setManageUser] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", company: "", email: "", is_admin: false, organization_id: "" });
  const [planForm, setPlanForm] = useState({ plan: "free", days: 30 });
  const [creditForm, setCreditForm] = useState({ amount: 0, mode: "set" });
  const [serverForm, setServerForm] = useState({ server_id: "", locked: false });
  const [savingServer, setSavingServer] = useState(false);
  const [settings, setSettings] = useState(null);
  const [concurrency, setConcurrency] = useState("3");
  const [savingSettings, setSavingSettings] = useState(false);
  const [jobsDashboard, setJobsDashboard] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [retryingAllFailed, setRetryingAllFailed] = useState(false);
  const [retryingCampaignId, setRetryingCampaignId] = useState(null);
  const [adminTab, setAdminTab] = useState("overview");
  const [serverPanel, setServerPanel] = useState("delivery");
  const [sslDomains, setSslDomains] = useState([]);
  const [sslLoading, setSslLoading] = useState(false);
  const [sslHostInput, setSslHostInput] = useState("");
  const [sslIssuing, setSslIssuing] = useState(null);
  const [sendFailuresOpen, setSendFailuresOpen] = useState(false);
  const [sendFailuresCampaign, setSendFailuresCampaign] = useState(null);
  const [sendFailures, setSendFailures] = useState([]);
  const [sendFailuresPage, setSendFailuresPage] = useState(1);
  const [sendFailuresPages, setSendFailuresPages] = useState(1);
  const [sendFailuresTotal, setSendFailuresTotal] = useState(0);
  const [sendFailuresLimit, setSendFailuresLimit] = useState(50);
  const [sendFailuresLoading, setSendFailuresLoading] = useState(false);

  const userFetcher = useCallback((page, limit) => api.get("/admin/users", { params: { page, limit } }), []);

  const {
    items: users,
    total: userTotal,
    pages: userPages,
    page: userPage,
    limit: userLimit,
    setPage: setUserPage,
    setLimit: setUserLimit,
    loading: usersLoading,
    reload: reloadUsers,
  } = usePaginatedList(userFetcher, []);

  const loadPlans = async () => {
    const { data } = await api.get("/admin/plans");
    setPlans(data.plans || []);
  };

  const loadOrganizations = async () => {
    setOrgsLoading(true);
    try {
      const { data } = await api.get("/admin/organizations");
      setOrganizations(data.organizations || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load organizations");
    } finally {
      setOrgsLoading(false);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      /* polling errors are non-fatal */
    }
  }, []);

  const loadMeta = async () => {
    setMetaLoading(true);
    try {
      const [s, srv] = await Promise.all([
        api.get("/admin/stats", { timeout: 120000 }),
        api.get("/admin/delivery-servers", { timeout: 60000 }),
      ]);
      setStats(s.data);
      setServers(srv.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load admin overview (stats may still be computing)");
      setStats((prev) => prev || EMPTY_ADMIN_STATS);
      setServers((prev) => prev || []);
    } finally {
      setMetaLoading(false);
    }
    api.get("/admin/settings", { timeout: 60000 }).catch(() => ({ data: null })).then((sett) => {
      if (sett?.data) {
        setSettings(sett.data);
        setConcurrency(String(sett.data.max_concurrent_campaign_sends ?? 3));
      }
    });
    Promise.all([loadPlans(), loadOrganizations()]).catch(() => {});
  };

  const saveSettings = async () => {
    const n = parseInt(concurrency, 10);
    if (!Number.isFinite(n)) return toast.error("Enter a valid number");
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/settings", { max_concurrent_campaign_sends: n });
      setSettings(data);
      setConcurrency(String(data.max_concurrent_campaign_sends));
      toast.success(`Up to ${data.max_concurrent_campaign_sends} campaigns can send at once`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const resetPlanForm = () => {
    setPlanEditingId(null);
    setPlanEditorForm({
      id: "",
      name: "",
      price: "0",
      max_contacts: "1000",
      max_emails_per_month: "5000",
      features: "",
      is_active: true,
      sort_order: String(plans.length),
    });
  };

  const openPlanCreate = () => {
    resetPlanForm();
    setPlanEditOpen(true);
  };

  const openPlanEdit = (plan) => {
    setPlanEditingId(plan.id);
    setPlanEditorForm({
      id: plan.id,
      name: plan.name || "",
      price: String(plan.price ?? 0),
      max_contacts: String(plan.max_contacts ?? 0),
      max_emails_per_month: String(plan.max_emails_per_month ?? 0),
      features: (plan.features || []).join("\n"),
      is_active: plan.is_active !== false,
      sort_order: String(plan.sort_order ?? 0),
    });
    setPlanEditOpen(true);
  };

  const savePlan = async () => {
    if (!planEditorForm.name.trim()) return toast.error("Plan name is required");
    const payload = {
      name: planEditorForm.name.trim(),
      price: Number(planEditorForm.price) || 0,
      max_contacts: parseInt(planEditorForm.max_contacts, 10) || 0,
      max_emails_per_month: parseInt(planEditorForm.max_emails_per_month, 10) || 0,
      features: planEditorForm.features.split(/\r?\n/).map((l) => l.trim()).filter(Boolean),
      is_active: planEditorForm.is_active,
      sort_order: parseInt(planEditorForm.sort_order, 10) || 0,
    };
    setSavingPlan(true);
    try {
      if (planEditingId) {
        await api.put(`/admin/plans/${planEditingId}`, payload);
        toast.success("Plan updated");
      } else {
        const id = (planEditorForm.id || planEditorForm.name).trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
        if (!id) return toast.error("Plan id is required");
        await api.post("/admin/plans", { ...payload, id });
        toast.success("Plan created");
      }
      setPlanEditOpen(false);
      resetPlanForm();
      loadPlans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save plan");
    } finally {
      setSavingPlan(false);
    }
  };

  const removePlan = async (plan) => {
    const ok = await confirm({
      title: `Delete plan "${plan.name}"?`,
      description: "Users on this plan must be moved first. This cannot be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      toast.success("Plan deleted");
      loadPlans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete plan");
    }
  };

  const load = () => {
    loadMeta();
    reloadUsers();
  };

  useEffect(() => { loadMeta(); }, []);

  useEffect(() => {
    if (adminTab !== "overview") return undefined;
    loadStats();
    const timer = setInterval(loadStats, 15000);
    return () => clearInterval(timer);
  }, [adminTab, loadStats]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ name: "", type: "mock", config: {}, is_active: true });
  };

  const openCreate = (kind = "delivery") => {
    setEditingId(null);
    const type = kind === "bounce" ? "bounce" : kind === "fbl" ? "fbl" : "smtp";
    setForm({ name: "", type, config: {}, is_active: true });
    setOpen(true);
  };

  const openEdit = (server) => {
    setEditingId(server.id);
    setForm({
      name: server.name,
      type: server.type,
      config: { ...(server.config || {}) },
      is_active: server.is_active,
    });
    setOpen(true);
  };

  const saveServer = async () => {
    if (!form.name) return toast.error("Name is required");
    try {
      if (editingId) {
        await api.put(`/admin/delivery-servers/${editingId}`, form);
        toast.success("Delivery server updated");
      } else {
        await api.post("/admin/delivery-servers", form);
        toast.success("Delivery server added");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const deleteServer = async (id) => {
    const ok = await confirm({
      title: "Delete delivery server?",
      description: "Users assigned to it will be reassigned on next send.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/admin/delivery-servers/${id}`);
    toast.success("Server deleted");
    load();
  };

  const sendTest = async () => {
    if (!testServer?.id) {
      toast.error("No delivery server selected");
      return;
    }
    const to_email = (testForm.to_email || "").trim();
    const from_email = (testForm.from_email || "").trim();
    if (!to_email || !from_email) {
      toast.error("Enter both To and From email addresses");
      return;
    }
    setTestingSend(true);
    try {
      const { data } = await api.post(
        `/admin/delivery-servers/${testServer.id}/test`,
        { ...testForm, to_email, from_email },
        { timeout: 120000, skipAuthRedirect: true },
      );
      if (data.ok) {
        const env = data.envelope_sender && data.envelope_sender !== data.from_email
          ? ` Return-Path: ${data.envelope_sender}${data.bounce_server ? ` (via ${data.bounce_server})` : ""}.`
          : data.bounce_server
            ? ` Bounce server: ${data.bounce_server}.`
            : " No bounce server linked — bounces may not be tracked.";
        toast.success(`Test email sent via ${data.type}.${env}`);
        setTestOpen(false);
      } else {
        toast.error(data.error ? `Test failed: ${data.error}` : "Test failed");
      }
    } catch (err) {
      toast.error(formatApiDetail(err, "Test failed"));
    } finally {
      setTestingSend(false);
    }
  };

  const openMetrics = async (server) => {
    try {
      const { data } = await api.get(`/admin/delivery-servers/${server.id}/metrics`);
      setMetrics(data);
      setMetricsOpen(true);
    } catch (err) {
      toast.error("Failed to load metrics");
    }
  };

  const showInboxTestResult = (data) => {
    setInboxTestResult(data);
    setInboxTestOpen(true);
  };

  const testInboxFromForm = async () => {
    if (!(form.config.imap_host || "").trim()) {
      return toast.error("Enter IMAP host before testing");
    }
    setTestingInboxForm(true);
    try {
      const { data } = await api.post("/admin/delivery-servers/test-inbox-config", {
        type: form.type,
        config: form.config,
        server_id: editingId || undefined,
      });
      showInboxTestResult(data);
      if (data.ok) {
        toast.success(`Connected — ${data.folder_total ?? 0} message(s) in ${data.folder || "INBOX"}`);
      } else {
        toast.error(data.error || "Connection failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connection test failed");
    } finally {
      setTestingInboxForm(false);
    }
  };

  const testImap = async (server) => {
    setTestingImapId(server.id);
    try {
      const { data } = await api.post(`/admin/delivery-servers/${server.id}/test-imap`);
      showInboxTestResult(data);
      if (data.ok) {
        toast.success(`Connected — ${data.folder_total ?? 0} message(s) in ${data.folder || "INBOX"}`);
      } else {
        const mailboxes = data.mailboxes?.length ? ` Available folders listed in details.` : "";
        toast.error((data.error || "Connection failed") + mailboxes);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connection test failed");
    } finally {
      setTestingImapId(null);
    }
  };

  const pollBounces = async (server) => {
    setPollingId(server.id);
    try {
      const { data } = await api.post(`/webhooks/admin/poll-bounces?server_id=${server.id}`);
      const hint = data.hint ? ` — ${data.hint}` : "";
      const errs = data.errors?.length ? ` (${data.errors[0]})` : "";
      const diagParts = [];
      if (data.folder_total != null) diagParts.push(`${data.folder_total} in folder`);
      if (data.search) {
        diagParts.push(
          Object.entries(data.search)
            .map(([k, v]) => `${k}=${v}`)
            .join(" "),
        );
      }
      const searchInfo = diagParts.length ? ` (${diagParts.join(", ")})` : "";
      const extra = [
        data.no_recipients ? `${data.no_recipients} unparsed` : null,
        data.unknown_contact ? `${data.unknown_contact} unknown contact` : null,
      ]
        .filter(Boolean)
        .join(", ");
      const extraStr = extra ? ` — ${extra}` : "";
      toast.success(
        `Polled ${data.folder || "INBOX"} — checked ${data.uids_checked ?? 0}, marked ${data.marked || 0}, skipped ${data.skipped || 0}${searchInfo}${extraStr}${hint}${errs}`,
        { duration: hint || data.errors?.length ? 10000 : 5000 },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Poll failed");
    } finally {
      setPollingId(null);
    }
  };

  const resetBouncePoll = async (server) => {
    const ok = await confirm({
      title: "Reset bounce poll cache?",
      description: "Clear bounce poll history for this server? Messages in the bounce inbox will be re-processed on the next poll.",
      confirmText: "Reset",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/webhooks/admin/reset-bounce-imap-processed?server_id=${server.id}`);
      toast.success(`Reset bounce poll cache (${data.deleted || 0} entries)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const toggleServerActive = async (server) => {
    try {
      await api.post(`/admin/delivery-servers/${server.id}/active`, { is_active: !server.is_active });
      setServers((prev) => prev.map((s) => (s.id === server.id ? { ...s, is_active: !server.is_active } : s)));
      toast.success(!server.is_active ? "Server activated" : "Server deactivated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const createUser = async () => {
    if (!userForm.email || !userForm.password || !userForm.name) return toast.error("Email, password, and name are required");
    try {
      const payload = {
        ...userForm,
        organization_id: userForm.organization_id || null,
        email_credits: userForm.email_credits === "" ? null : Number(userForm.email_credits),
      };
      await api.post("/admin/users", payload);
      toast.success("User created");
      setUserCreateOpen(false);
      setUserForm({ email: "", password: "", name: "", company: "", is_admin: false, plan: "free", email_credits: "", organization_id: "" });
      load();
      if (adminTab === "organizations") loadOrganizations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const createOrganization = async () => {
    if (!orgCreateForm.name.trim()) return toast.error("Organization name is required");
    try {
      await api.post("/admin/organizations", {
        name: orgCreateForm.name.trim(),
        default_tracking_domain: orgCreateForm.default_tracking_domain.trim() || null,
      });
      toast.success("Organization created");
      setOrgCreateForm({ name: "", default_tracking_domain: "" });
      loadOrganizations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const updateOrganizationTracking = async (org) => {
    try {
      await api.put(`/admin/organizations/${org.id}`, {
        default_tracking_domain: (org.default_tracking_domain || "").trim() || null,
      });
      toast.success("Organization updated");
      loadOrganizations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openOrgMembers = async (org) => {
    setOrgMembersTarget(org);
    setOrgMembersOpen(true);
    try {
      const { data } = await api.get("/admin/users", { params: { page: 1, limit: 500 } });
      const list = data.items || data || [];
      setAllUsersForOrg(list);
      setOrgMemberIds(list.filter((u) => u.organization_id === org.id).map((u) => u.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
    }
  };

  const saveOrgMembers = async () => {
    if (!orgMembersTarget) return;
    setSavingOrgMembers(true);
    try {
      const previouslyInOrg = allUsersForOrg.filter((u) => u.organization_id === orgMembersTarget.id).map((u) => u.id);
      const toRemove = previouslyInOrg.filter((id) => !orgMemberIds.includes(id));
      const toAdd = orgMemberIds.filter((id) => !previouslyInOrg.includes(id));

      await Promise.all(
        toRemove.map((userId) => api.put(`/admin/users/${userId}/organization`, { organization_id: null })),
      );
      if (toAdd.length) {
        await api.post(`/admin/organizations/${orgMembersTarget.id}/assign-users`, { user_ids: toAdd });
      }
      toast.success(`Updated ${orgMembersTarget.name} members`);
      setOrgMembersOpen(false);
      loadOrganizations();
      reloadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSavingOrgMembers(false);
    }
  };

  const openManage = (u) => {
    setManageUser(u);
    setEditForm({
      name: u.name || "",
      company: u.company || "",
      email: u.email || "",
      is_admin: !!u.is_admin,
      organization_id: u.organization_id || "",
    });
    setPlanForm({ plan: u.plan || "free", days: 30 });
    setCreditForm({ amount: 0, mode: "set" });
    setServerForm({
      server_id: u.delivery_server_id || "",
      locked: !!u.delivery_server_locked,
    });
    setManageOpen(true);
  };

  const saveUser = async () => {
    try {
      const payload = {
        ...editForm,
        organization_id: editForm.organization_id || null,
      };
      const { data } = await api.put(`/admin/users/${manageUser.id}`, payload);
      toast.success("Profile updated");
      setManageUser((m) => ({ ...m, ...data }));
      load();
      if (adminTab === "organizations") loadOrganizations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const deleteCustomer = async (u) => {
    if (u.is_admin) {
      return toast.error("Remove admin access before deleting this account");
    }
    const ok = await confirm({
      title: `Delete customer "${u.name}"?`,
      description: `This permanently removes ${u.email} and all their audiences, contacts, campaigns, templates, automations, suppressions, and sending domains. This cannot be undone.`,
      confirmText: "Continue",
      destructive: true,
    });
    if (!ok) return;
    const typed = window.prompt(`Type the customer email to confirm:\n${u.email}`);
    if ((typed || "").trim().toLowerCase() !== (u.email || "").trim().toLowerCase()) {
      return toast.error("Email did not match — deletion cancelled");
    }
    try {
      await api.delete(`/admin/users/${u.id}`, { data: { confirm_email: u.email } });
      toast.success(`Deleted ${u.email}`);
      if (manageUser?.id === u.id) setManageUser(null);
      load();
      if (adminTab === "organizations") loadOrganizations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete user");
    }
  };

  const deleteOrganization = async (org) => {
    const ok = await confirm({
      title: `Delete organization "${org.name}"?`,
      description: `This deletes the organization, its shared sending domains, and permanently deletes every member customer account (admin accounts are only unlinked). All member data is removed. This cannot be undone.`,
      confirmText: "Continue",
      destructive: true,
    });
    if (!ok) return;
    const typed = window.prompt(`Type the organization name to confirm:\n${org.name}`);
    if ((typed || "").trim() !== (org.name || "").trim()) {
      return toast.error("Organization name did not match — deletion cancelled");
    }
    try {
      const { data } = await api.delete(`/admin/organizations/${org.id}`, {
        data: { confirm_name: org.name, delete_member_accounts: true },
      });
      const n = data.members_deleted?.length ?? 0;
      toast.success(`Organization deleted${n ? ` (${n} member account${n === 1 ? "" : "s"} removed)` : ""}`);
      if (orgMembersTarget?.id === org.id) setOrgMembersTarget(null);
      loadOrganizations();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete organization");
    }
  };

  const toggleSuspend = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
      toast.success(!u.suspended ? "User suspended" : "User reactivated");
      setManageUser((m) => (m && m.id === u.id ? { ...m, suspended: !u.suspended } : m));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const allocatePlan = async () => {
    try {
      await api.post(`/admin/users/${manageUser.id}/plan`, { plan: planForm.plan, days: Number(planForm.days) || 30 });
      toast.success("Plan allocated");
      setManageUser((m) => ({ ...m, plan: planForm.plan }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const allocateCredits = async () => {
    try {
      const { data } = await api.post(`/admin/users/${manageUser.id}/credits`, { amount: Number(creditForm.amount) || 0, mode: creditForm.mode });
      toast.success("Email credits updated");
      setManageUser((m) => ({ ...m, email_credits: data.email_credits }));
      setCreditForm({ amount: 0, mode: "set" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const saveServerAssignment = async () => {
    if (serverForm.locked && !serverForm.server_id) {
      return toast.error("Select a delivery server before locking");
    }
    setSavingServer(true);
    try {
      const { data } = await api.post(`/admin/users/${manageUser.id}/delivery-server`, {
        server_id: serverForm.server_id || null,
        locked: serverForm.locked,
      });
      toast.success(serverForm.locked ? "SMTP server locked for this customer" : "SMTP server assignment updated");
      setManageUser((m) => ({ ...m, ...data }));
      reloadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update delivery server");
    } finally {
      setSavingServer(false);
    }
  };

  const sendServers = servers.filter((s) => DELIVERY_TYPES.includes(s.type));

  const loadSslDomains = useCallback(async () => {
    setSslLoading(true);
    try {
      const { data } = await api.get("/admin/ssl/tracking-domains");
      setSslDomains(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load tracking SSL status");
    } finally {
      setSslLoading(false);
    }
  }, []);

  const issueSsl = async (hostname) => {
    const host = (hostname || sslHostInput || "").trim();
    if (!host) return toast.error("Enter a hostname");
    setSslIssuing(host);
    try {
      await api.post("/admin/ssl/issue", { hostname: host });
      toast.success(`SSL issued for ${host}`);
      setSslHostInput("");
      loadSslDomains();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SSL issuance failed");
    } finally {
      setSslIssuing(null);
    }
  };

  useEffect(() => {
    if (adminTab === "organizations") {
      loadOrganizations();
    }
    if (adminTab !== "ssl") return undefined;
    loadSslDomains();
    return undefined;
  }, [adminTab, loadSslDomains]);

  const creditLabel = (v) => (v === null || v === undefined ? "Unlimited" : Number(v).toLocaleString());

  const loadJobsDashboard = useCallback(async () => {
    setJobsLoading(true);
    try {
      const { data } = await api.get("/admin/jobs/dashboard");
      setJobsDashboard(data);
    } catch {
      toast.error("Failed to load job queue");
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const loadSendFailures = useCallback(async (campaignId, page = 1, limit = sendFailuresLimit) => {
    setSendFailuresLoading(true);
    try {
      const { data } = await api.get(`/admin/campaigns/${campaignId}/send-failures`, {
        params: { page, limit },
      });
      setSendFailures(data.items || []);
      setSendFailuresPage(data.page || page);
      setSendFailuresPages(data.pages || 1);
      setSendFailuresTotal(data.total ?? 0);
      setSendFailuresLimit(data.limit || limit);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load send failures");
      setSendFailures([]);
    } finally {
      setSendFailuresLoading(false);
    }
  }, [sendFailuresLimit]);

  const openSendFailures = (campaignId, campaignName) => {
    setSendFailuresCampaign({ id: campaignId, name: campaignName || "Campaign" });
    setSendFailuresOpen(true);
    setSendFailuresPage(1);
    loadSendFailures(campaignId, 1, sendFailuresLimit);
  };

  const retryFailedCampaign = async (campaignId) => {
    setRetryingCampaignId(campaignId);
    try {
      const { data } = await api.post(`/admin/campaigns/${campaignId}/retry`);
      toast.success(`Queued "${data.campaign_name || "campaign"}" for sending`);
      loadJobsDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to retry campaign");
    } finally {
      setRetryingCampaignId(null);
    }
  };

  const retryAllFailedCampaigns = async () => {
    const count = jobsDashboard?.campaigns_failed_count ?? jobsDashboard?.campaigns_failed?.length ?? 0;
    if (count === 0) {
      toast.info("No failed campaigns to retry");
      return;
    }
    const ok = await confirm({
      title: "Retry all failed campaigns?",
      description: `This will re-queue ${count} failed or stuck campaign${count === 1 ? "" : "s"} across all customers. Sends resume from where they left off when possible.`,
      confirmText: "Retry all",
    });
    if (!ok) return;
    setRetryingAllFailed(true);
    try {
      const { data } = await api.post("/admin/campaigns/retry-failed");
      if (data.queued > 0) {
        toast.success(`Queued ${data.queued} of ${data.total} campaign${data.total === 1 ? "" : "s"} for sending`);
      } else {
        toast.warning(`No campaigns queued (${data.skipped ?? 0} skipped)`);
      }
      loadJobsDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to retry campaigns");
    } finally {
      setRetryingAllFailed(false);
    }
  };

  useEffect(() => {
    if (adminTab !== "jobs") return undefined;
    loadJobsDashboard();
    const timer = setInterval(loadJobsDashboard, 10000);
    return () => clearInterval(timer);
  }, [adminTab, loadJobsDashboard]);

  const jobStatusStyle = (status) => {
    const map = {
      running: { bg: "#FEF3C7", text: "#B45309" },
      pending: { bg: "#DBEAFE", text: "#1E40AF" },
      staging: { bg: "#E0E7FF", text: "#3730A3" },
      completed: { bg: "#D1FAE5", text: "#065F46" },
      failed: { bg: "#FEE2E2", text: "#991B1B" },
      cancelled: { bg: "#F3F4F6", text: "#374151" },
    };
    return map[status] || { bg: "#F3F4F6", text: "#374151" };
  };

  const jobTypeLabel = (t) => (t === "send_campaign" ? "Campaign send" : t === "import_contacts" ? "Contact import" : t);
  const overviewStats = stats || EMPTY_ADMIN_STATS;

  return (
    <div data-testid="admin-page">
      <PageHeader
        title="Admin console"
        subtitle="Platform-wide stats, users, and email delivery infrastructure."
        action={<div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"><Shield className="h-4 w-4" /> Admin only</div>}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {metaLoading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading overview…</div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-8" value={adminTab} onValueChange={setAdminTab}>
            <TabsList className="rounded-sm">
              <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="jobs" data-testid="admin-tab-jobs">Jobs &amp; queue</TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
              <TabsTrigger value="organizations" data-testid="admin-tab-organizations">Organizations</TabsTrigger>
              <TabsTrigger value="plans" data-testid="admin-tab-plans">Plans</TabsTrigger>
              <TabsTrigger value="servers" data-testid="admin-tab-servers">Delivery servers</TabsTrigger>
              <TabsTrigger value="ssl" data-testid="admin-tab-ssl">Tracking SSL</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {settings && (
                <div className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm" data-testid="admin-campaign-concurrency">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                      <p className="label-overline mb-2">Campaign sending</p>
                      <h3 className="font-display font-bold text-xl tracking-tight">Concurrent campaigns</h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-xl">
                        How many scheduled or background campaign sends can run at the same time.
                        Currently <strong>{settings.running_campaign_jobs}</strong> running,{" "}
                        <strong>{settings.pending_campaign_jobs}</strong> queued.
                      </p>
                    </div>
                    <div className="flex items-end gap-3">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">Max at once</Label>
                        <Input
                          type="number"
                          min={settings.min_concurrent_campaign_sends ?? 1}
                          max={settings.max_concurrent_campaign_sends_limit ?? 20}
                          value={concurrency}
                          onChange={(e) => setConcurrency(e.target.value)}
                          className="mt-2 w-28 rounded-sm"
                          data-testid="max-concurrent-campaigns-input"
                        />
                        <p className="text-xs text-neutral-400 mt-1">
                          {settings.min_concurrent_campaign_sends ?? 1}–{settings.max_concurrent_campaign_sends_limit ?? 20}
                        </p>
                      </div>
                      <Button
                        onClick={saveSettings}
                        disabled={savingSettings}
                        className="rounded-sm btn-brand font-semibold h-10"
                        data-testid="save-campaign-concurrency-button"
                      >
                        {savingSettings ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <Stat label="Users" value={overviewStats.total_users} Icon={UsersIcon} />
                <Stat label="Audiences" value={overviewStats.total_audiences} Icon={UsersIcon} />
                <Stat label="Contacts" value={overviewStats.total_contacts} Icon={Mail} />
                <Stat label="Campaigns" value={overviewStats.total_campaigns} Icon={Send} />
                <Stat label="Active servers" value={overviewStats.active_servers} Icon={Server} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Stat label="Emails sent" value={overviewStats.total_recipients} Icon={Send} />
                <Stat label="Total opens" value={overviewStats.total_opens} Icon={Activity} />
                <div className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm">
                  <span className="label-overline">Avg open rate</span>
                  <div className="mt-6 font-display font-black tracking-tighter text-4xl">{overviewStats.open_rate}<span className="text-neutral-400 text-2xl">%</span></div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="jobs" className="space-y-6" data-testid="admin-jobs-tab">
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  className="rounded-sm"
                  onClick={retryAllFailedCampaigns}
                  disabled={retryingAllFailed || jobsLoading || !(jobsDashboard?.campaigns_failed_count ?? jobsDashboard?.campaigns_failed?.length)}
                  data-testid="admin-retry-all-failed"
                >
                  <RotateCcw className={`h-4 w-4 mr-2 ${retryingAllFailed ? "animate-spin" : ""}`} />
                  Retry all failed
                </Button>
                <Button variant="outline" className="rounded-sm" onClick={loadJobsDashboard} disabled={jobsLoading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${jobsLoading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>

              {jobsLoading && !jobsDashboard ? (
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading queue...</div>
              ) : jobsDashboard ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <Stat label="Sending now" value={jobsDashboard.queue?.running_campaign_jobs ?? 0} Icon={Send} />
                    <Stat label="Send queue" value={jobsDashboard.queue?.pending_campaign_jobs ?? 0} Icon={ListTodo} />
                    <Stat label="Import running" value={jobsDashboard.queue?.running_import_jobs ?? 0} Icon={Mail} />
                    <Stat label="Import queue" value={jobsDashboard.queue?.pending_import_jobs ?? 0} Icon={ListTodo} />
                    <Stat label="Send slots free" value={jobsDashboard.queue?.campaign_send_slots_free ?? 0} Icon={Activity} />
                    <Stat label="Failed (24h)" value={jobsDashboard.queue?.failed_jobs_24h ?? 0} Icon={Activity} />
                    <Stat label="Failed campaigns" value={jobsDashboard.campaigns_failed_count ?? jobsDashboard.campaigns_failed?.length ?? 0} Icon={AlertTriangle} />
                  </div>

                  <div className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm">
                    <p className="label-overline mb-2">Worker</p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Background worker runs every {jobsDashboard.queue?.background_worker_interval_seconds ?? 15}s.
                      Max <strong>{jobsDashboard.queue?.max_concurrent_campaign_sends}</strong> campaign sends at once
                      ({jobsDashboard.queue?.campaign_send_slots_used} in use).
                      {jobsDashboard.queue?.in_memory_running_job_ids?.length > 0 && (
                        <> In-memory tasks: {jobsDashboard.queue.in_memory_running_job_ids.length}.</>
                      )}
                    </p>
                  </div>

                  {(jobsDashboard.campaigns_failed?.length ?? 0) > 0 && (
                    <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
                      <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h3 className="font-display font-bold text-lg">Failed / stuck campaigns</h3>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">All customers — retry resumes from last progress when available</p>
                        </div>
                        <Button
                          size="sm"
                          className="rounded-sm btn-brand"
                          onClick={retryAllFailedCampaigns}
                          disabled={retryingAllFailed}
                          data-testid="admin-retry-all-failed-inline"
                        >
                          <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${retryingAllFailed ? "animate-spin" : ""}`} />
                          Retry all ({jobsDashboard.campaigns_failed.length})
                        </Button>
                      </div>
                      <table className="w-full min-w-[800px]">
                        <thead>
                          <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                            <th className="text-left px-6 py-3 label-overline">Campaign</th>
                            <th className="text-left px-6 py-3 label-overline">User</th>
                            <th className="text-left px-6 py-3 label-overline">Status</th>
                            <th className="text-right px-6 py-3 label-overline">Progress</th>
                            <th className="text-right px-6 py-3 label-overline"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobsDashboard.campaigns_failed.map((c) => {
                            const sp = c.send_progress || {};
                            const st = jobStatusStyle(c.status);
                            return (
                              <tr key={c.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                <td className="px-6 py-4">
                                  <Link to={`/campaigns/${c.id}`} className="font-semibold hover:underline">{c.name}</Link>
                                  {c.send_error && (
                                    <div className={`text-xs mt-1 max-w-md ${c.rate_limit_waiting ? "text-amber-700 dark:text-amber-400" : "text-red-600"}`}>
                                      {c.send_error}
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">{c.user_email || "—"}</td>
                                <td className="px-6 py-4">
                                  <span className="inline-flex px-2 py-0.5 rounded-sm text-xs font-bold uppercase" style={{ backgroundColor: st.bg, color: st.text }}>{c.status}</span>
                                </td>
                                <td className="px-6 py-4 text-right text-sm font-medium">
                                  {sp.total ? (
                                    <>
                                      {sp.sent ?? 0} / {sp.total}
                                      {(sp.failed ?? 0) > 0 && (
                                        <span className="block text-xs text-red-600 dark:text-red-400">{sp.failed} failed</span>
                                      )}
                                    </>
                                  ) : "—"}
                                </td>
                                <td className="px-6 py-4 text-right space-x-2 whitespace-nowrap">
                                  {(sp.failed ?? 0) > 0 && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="rounded-sm h-8"
                                      onClick={() => openSendFailures(c.id, c.name)}
                                      data-testid={`admin-failures-campaign-${c.id}`}
                                    >
                                      Failures
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-sm h-8"
                                    onClick={() => retryFailedCampaign(c.id)}
                                    disabled={retryingCampaignId === c.id || retryingAllFailed}
                                    data-testid={`admin-retry-campaign-${c.id}`}
                                  >
                                    <RotateCcw className={`h-3.5 w-3.5 mr-1 ${retryingCampaignId === c.id ? "animate-spin" : ""}`} />
                                    Retry
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {(jobsDashboard.campaigns_active?.length ?? 0) > 0 && (
                    <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
                      <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
                        <h3 className="font-display font-bold text-lg">Campaigns sending / scheduled / paused</h3>
                      </div>
                      <table className="w-full min-w-[720px]">
                        <thead>
                          <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                            <th className="text-left px-6 py-3 label-overline">Campaign</th>
                            <th className="text-left px-6 py-3 label-overline">User</th>
                            <th className="text-left px-6 py-3 label-overline">Status</th>
                            <th className="text-right px-6 py-3 label-overline">Progress</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobsDashboard.campaigns_active.map((c) => {
                            const sp = c.send_progress || {};
                            const st = jobStatusStyle(c.status);
                            return (
                              <tr key={c.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                <td className="px-6 py-4">
                                  <Link to={`/campaigns/${c.id}`} className="font-semibold hover:underline">{c.name}</Link>
                                  {c.send_error && <div className="text-xs text-red-600 mt-1">{c.send_error}</div>}
                                </td>
                                <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">{c.user_email || "—"}</td>
                                <td className="px-6 py-4">
                                  <span className="inline-flex px-2 py-0.5 rounded-sm text-xs font-bold uppercase" style={{ backgroundColor: st.bg, color: st.text }}>{c.status}</span>
                                </td>
                                <td className="px-6 py-4 text-right text-sm font-medium">
                                  {c.status === "sending" && sp.total ? `${sp.sent ?? 0} / ${sp.total}` : c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
                    <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
                      <h3 className="font-display font-bold text-lg">Active jobs</h3>
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">{jobsDashboard.active_jobs?.length ?? 0} in queue or running</span>
                    </div>
                    {(jobsDashboard.active_jobs?.length ?? 0) === 0 ? (
                      <p className="px-6 py-8 text-sm text-neutral-500 dark:text-neutral-400">No active background jobs.</p>
                    ) : (
                      <table className="w-full min-w-[800px]">
                        <thead>
                          <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                            <th className="text-left px-6 py-3 label-overline">Type</th>
                            <th className="text-left px-6 py-3 label-overline">Target</th>
                            <th className="text-left px-6 py-3 label-overline">User</th>
                            <th className="text-left px-6 py-3 label-overline">Status</th>
                            <th className="text-right px-6 py-3 label-overline">Progress</th>
                            <th className="text-left px-6 py-3 label-overline">Started</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobsDashboard.active_jobs.map((j) => {
                            const st = jobStatusStyle(j.status);
                            const target = j.campaign_name || j.audience_name || j.campaign_id || j.audience_id || "—";
                            const prog = j.type === "import_contacts"
                              ? `${j.processed ?? 0} / ${j.total ?? "?"} (${j.added ?? 0} added)`
                              : j.campaign_send_progress?.total
                                ? `${j.campaign_send_progress.sent ?? 0} / ${j.campaign_send_progress.total}`
                                : j.sent != null ? `${j.sent} sent` : "—";
                            return (
                              <tr key={j.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                <td className="px-6 py-4 text-sm">{jobTypeLabel(j.type)}</td>
                                <td className="px-6 py-4 text-sm font-medium">
                                  {j.campaign_id ? <Link to={`/campaigns/${j.campaign_id}`} className="hover:underline">{target}</Link> : target}
                                </td>
                                <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">{j.user_email || "—"}</td>
                                <td className="px-6 py-4">
                                  <span className="inline-flex px-2 py-0.5 rounded-sm text-xs font-bold uppercase" style={{ backgroundColor: st.bg, color: st.text }}>
                                    {j.in_memory_running ? "running" : j.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-right text-sm">{prog}</td>
                                <td className="px-6 py-4 text-xs text-neutral-500 dark:text-neutral-400">{j.started_at ? new Date(j.started_at).toLocaleString() : "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
                    <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
                      <h3 className="font-display font-bold text-lg">Recent completed / failed</h3>
                    </div>
                    {(jobsDashboard.recent_jobs?.length ?? 0) === 0 ? (
                      <p className="px-6 py-8 text-sm text-neutral-500 dark:text-neutral-400">No recent job history.</p>
                    ) : (
                      <table className="w-full min-w-[800px]">
                        <thead>
                          <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                            <th className="text-left px-6 py-3 label-overline">Type</th>
                            <th className="text-left px-6 py-3 label-overline">Target</th>
                            <th className="text-left px-6 py-3 label-overline">Status</th>
                            <th className="text-right px-6 py-3 label-overline">Result</th>
                            <th className="text-left px-6 py-3 label-overline">Finished</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobsDashboard.recent_jobs.map((j) => {
                            const st = jobStatusStyle(j.status);
                            const target = j.campaign_name || j.audience_name || "—";
                            const result = j.type === "import_contacts"
                              ? `${j.added ?? 0} added, ${j.skipped ?? 0} skipped`
                              : `${j.sent ?? 0} sent${j.failed ? `, ${j.failed} failed` : ""}`;
                            const showFailures = j.type === "send_campaign" && j.campaign_id && (j.failed > 0);
                            return (
                              <tr key={j.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                <td className="px-6 py-4 text-sm">{jobTypeLabel(j.type)}</td>
                                <td className="px-6 py-4 text-sm">
                                  {j.campaign_id ? (
                                    <Link to={`/campaigns/${j.campaign_id}`} className="hover:underline">{target}</Link>
                                  ) : target}
                                </td>
                                <td className="px-6 py-4">
                                  <span className="inline-flex px-2 py-0.5 rounded-sm text-xs font-bold uppercase" style={{ backgroundColor: st.bg, color: st.text }}>{j.status}</span>
                                  {j.error && <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={j.error}>{j.error}</div>}
                                </td>
                                <td className="px-6 py-4 text-right text-sm">
                                  <div>{result}</div>
                                  {showFailures && (
                                    <Button
                                      type="button"
                                      variant="link"
                                      className="h-auto p-0 text-xs font-semibold text-[#002FA7] dark:text-blue-400"
                                      onClick={() => openSendFailures(j.campaign_id, j.campaign_name || target)}
                                      data-testid={`admin-failures-job-${j.id}`}
                                    >
                                      View failed emails
                                    </Button>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-xs text-neutral-500 dark:text-neutral-400">{j.completed_at ? new Date(j.completed_at).toLocaleString() : "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              ) : null}
            </TabsContent>

            <TabsContent value="users" className="space-y-4">
              <div className="flex justify-end">
                <Button className="rounded-sm btn-brand font-semibold" onClick={() => setUserCreateOpen(true)} data-testid="add-user-button">
                  <Plus className="h-4 w-4 mr-2" /> New user
                </Button>
              </div>
              <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
                <table className="w-full" data-testid="admin-users-table">
                  <thead>
                    <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                      <th className="text-left px-6 py-3 label-overline">User</th>
                      <th className="text-left px-6 py-3 label-overline">Company</th>
                      <th className="text-left px-6 py-3 label-overline">Organization</th>
                      <th className="text-left px-6 py-3 label-overline">Plan</th>
                      <th className="text-left px-6 py-3 label-overline">SMTP</th>
                      <th className="text-right px-6 py-3 label-overline">Credits</th>
                      <th className="text-left px-6 py-3 label-overline">Status</th>
                      <th className="text-right px-6 py-3 label-overline">Contacts</th>
                      <th className="text-right px-6 py-3 label-overline">Campaigns</th>
                      <th className="text-right px-6 py-3 label-overline">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-900" data-testid={`user-row-${u.id}`}>
                        <td className="px-6 py-4">
                          <div className="font-semibold flex items-center gap-1.5">
                            {u.name}
                            {u.is_admin && <Shield className="h-3.5 w-3.5" style={{ color: "var(--brand-primary)" }} />}
                          </div>
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">{u.email}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">{u.company || "—"}</td>
                        <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">{u.organization_name || "—"}</td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider bg-neutral-100 text-neutral-700 dark:text-neutral-300">{u.plan || "free"}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">
                          {u.delivery_server_locked ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800">
                              <Lock className="h-3 w-3" />
                              {u.delivery_server_name || "Locked"}
                            </span>
                          ) : (
                            u.delivery_server_name || "Auto"
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium">{creditLabel(u.email_credits)}</td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider" style={{
                            backgroundColor: u.suspended ? "#FEE2E2" : "#D1FAE5",
                            color: u.suspended ? "#991B1B" : "#065F46",
                          }}>
                            {u.suspended ? "Suspended" : "Active"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium">{u.contacts}</td>
                        <td className="px-6 py-4 text-right text-sm font-medium">{u.campaigns}</td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <div className="inline-flex gap-2">
                            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => openManage(u)} data-testid={`user-manage-${u.id}`}>
                              <Pencil className="h-3 w-3 mr-1" /> Manage
                            </Button>
                            <Button size="sm" variant="outline" className={`rounded-sm ${u.suspended ? "text-emerald-700" : "text-red-600"}`} onClick={() => toggleSuspend(u)} data-testid={`user-suspend-${u.id}`}>
                              {u.suspended ? <UserCheck className="h-3 w-3" /> : <UserX className="h-3 w-3" />}
                            </Button>
                            {!u.is_admin && (
                              <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => deleteCustomer(u)} data-testid={`user-delete-${u.id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <ListPagination
                  page={userPage}
                  pages={userPages}
                  total={userTotal}
                  limit={userLimit}
                  onPageChange={setUserPage}
                  onLimitChange={setUserLimit}
                />
              </div>
            </TabsContent>

            <TabsContent value="organizations" className="space-y-6" data-testid="admin-organizations-tab">
              <div className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm space-y-4">
                <div>
                  <p className="label-overline mb-2">Team setup</p>
                  <h3 className="font-display font-bold text-xl tracking-tight">Create organization</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-2xl">
                    One client with many user accounts? Create an organization, assign all users to it, then add the sending domain once with &quot;Share with team&quot; enabled. Every member can send from that domain.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Organization name</Label>
                    <Input
                      value={orgCreateForm.name}
                      onChange={(e) => setOrgCreateForm({ ...orgCreateForm, name: e.target.value })}
                      className="mt-2 rounded-sm"
                      placeholder="Acme Corp"
                      data-testid="org-create-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Default tracking domain (optional)</Label>
                    <Input
                      value={orgCreateForm.default_tracking_domain}
                      onChange={(e) => setOrgCreateForm({ ...orgCreateForm, default_tracking_domain: e.target.value })}
                      className="mt-2 rounded-sm"
                      placeholder="track.acme.com"
                      data-testid="org-create-tracking"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={createOrganization} className="rounded-sm btn-brand font-semibold w-full md:w-auto" data-testid="org-create-submit">
                      <Plus className="h-4 w-4 mr-2" /> Create organization
                    </Button>
                  </div>
                </div>
              </div>

              {orgsLoading ? (
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading organizations...</div>
              ) : organizations.length === 0 ? (
                <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center text-sm text-neutral-500 dark:text-neutral-400">
                  No organizations yet. Create one to share a single sending domain across multiple accounts.
                </div>
              ) : (
                <div className="space-y-4">
                  {organizations.map((org) => (
                    <div key={org.id} className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm" data-testid={`org-card-${org.id}`}>
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                        <div className="flex items-start gap-3">
                          <Building2 className="h-5 w-5 text-neutral-600 dark:text-neutral-400 mt-0.5" />
                          <div>
                            <h3 className="font-display font-bold text-xl tracking-tight">{org.name}</h3>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                              {org.user_count ?? 0} user(s) · {org.domain_count ?? 0} shared domain(s)
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openOrgMembers(org)} data-testid={`org-members-${org.id}`}>
                            <UsersIcon className="h-3.5 w-3.5 mr-1" /> Manage members
                          </Button>
                          <Button variant="outline" size="sm" className="rounded-sm text-red-600" onClick={() => deleteOrganization(org)} data-testid={`org-delete-${org.id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete org & data
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                        <div className="md:col-span-2">
                          <Label className="text-xs font-bold uppercase tracking-wider">Default tracking domain</Label>
                          <Input
                            value={org.default_tracking_domain || ""}
                            onChange={(e) => setOrganizations((prev) => prev.map((o) => (o.id === org.id ? { ...o, default_tracking_domain: e.target.value } : o)))}
                            className="mt-2 rounded-sm"
                            placeholder="track.yourclient.com"
                            data-testid={`org-tracking-${org.id}`}
                          />
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Used when a member has no personal tracking domain set.</p>
                        </div>
                        <Button variant="outline" className="rounded-sm font-semibold" onClick={() => updateOrganizationTracking(org)} data-testid={`org-save-tracking-${org.id}`}>
                          Save tracking default
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="plans" className="space-y-4" data-testid="admin-plans-tab">
              <div className="flex justify-between items-center gap-4">
                <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-2xl">
                  Create and edit billing plans. Limits apply when plan limits are enabled. Prices are used for Stripe checkout.
                </p>
                <Button className="rounded-sm btn-brand font-semibold shrink-0" onClick={openPlanCreate} data-testid="add-plan-button">
                  <Plus className="h-4 w-4 mr-2" /> New plan
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((p) => (
                  <div key={p.id} className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm" data-testid={`admin-plan-card-${p.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-display font-bold text-xl tracking-tight">{p.name}</h3>
                          {!p.is_active && (
                            <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-neutral-100 dark:bg-neutral-800 text-neutral-500">Hidden</span>
                          )}
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 font-mono">{p.id}</p>
                      </div>
                      <div className="font-display font-black text-2xl tracking-tighter">
                        {p.price <= 0 ? "Free" : `$${Number(p.price).toFixed(0)}`}
                      </div>
                    </div>
                    <div className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                      <div>{p.max_contacts?.toLocaleString()} contacts</div>
                      <div>{p.max_emails_per_month?.toLocaleString()} emails / month</div>
                    </div>
                    {(p.features || []).length > 0 && (
                      <ul className="text-xs text-neutral-500 dark:text-neutral-400 space-y-1 mb-4 list-disc pl-4">
                        {p.features.slice(0, 4).map((f) => <li key={f}>{f}</li>)}
                      </ul>
                    )}
                    <div className="flex gap-2 pt-4 border-t border-neutral-100 dark:border-neutral-800">
                      <Button size="sm" variant="outline" className="rounded-sm flex-1" onClick={() => openPlanEdit(p)} data-testid={`plan-edit-${p.id}`}>
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      {p.id !== "free" && (
                        <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => removePlan(p)} data-testid={`plan-delete-${p.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="servers" className="space-y-4">
              <Tabs value={serverPanel} onValueChange={setServerPanel}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <TabsList>
                    <TabsTrigger value="delivery">Delivery (SMTP)</TabsTrigger>
                    <TabsTrigger value="bounce">Bounce servers</TabsTrigger>
                    <TabsTrigger value="fbl">FBL servers</TabsTrigger>
                  </TabsList>
                  <Button
                    className="rounded-sm btn-brand font-semibold"
                    onClick={() => openCreate(serverPanel === "bounce" ? "bounce" : serverPanel === "fbl" ? "fbl" : "delivery")}
                    data-testid="add-server-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {serverPanel === "bounce" ? "Add bounce server" : serverPanel === "fbl" ? "Add FBL server" : "Add delivery server"}
                  </Button>
                </div>

                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>
                        {form.type === "bounce"
                          ? (editingId ? "Edit bounce server" : "Add bounce server")
                          : form.type === "fbl"
                            ? (editingId ? "Edit FBL server" : "Add FBL server")
                            : (editingId ? "Edit delivery server" : "Add delivery server")}
                      </DialogTitle>
                    </DialogHeader>
                    <DialogScrollBody>
                    <div className="space-y-4 py-2">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">Name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" placeholder={form.type === "bounce" ? "Primary bounce" : form.type === "fbl" ? "Primary FBL" : "Primary SMTP"} data-testid="server-name-input" />
                      </div>

                      {DELIVERY_TYPES.includes(form.type) && (
                        <>
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider">Type</Label>
                            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, config: { bounce_server_id: form.config.bounce_server_id, fbl_server_id: form.config.fbl_server_id } })}>
                              <SelectTrigger className="mt-2 rounded-sm" data-testid="server-type-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mock">Mock (testing)</SelectItem>
                                <SelectItem value="smtp">SMTP</SelectItem>
                                <SelectItem value="sendgrid">SendGrid</SelectItem>
                                <SelectItem value="resend">Resend</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {(TYPE_FIELDS[form.type] || []).map((f) => (
                            <div key={f.key}>
                              <Label className="text-xs font-bold uppercase tracking-wider">{f.label}</Label>
                              <Input
                                type={f.type || "text"}
                                placeholder={f.placeholder}
                                value={form.config[f.key] || ""}
                                onChange={(e) => setForm({ ...form, config: { ...form.config, [f.key]: e.target.value } })}
                                className="mt-2 rounded-sm"
                                data-testid={`server-config-${f.key}`}
                              />
                            </div>
                          ))}
                          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-3 space-y-3">
                            <div className="label-overline">Linked servers (Mailwizz-style)</div>
                            <div>
                              <Label className="text-xs font-bold uppercase tracking-wider">Bounce server</Label>
                              <Select
                                value={form.config.bounce_server_id || "none"}
                                onValueChange={(v) => setForm({ ...form, config: { ...form.config, bounce_server_id: v === "none" ? "" : v } })}
                              >
                                <SelectTrigger className="mt-2 rounded-sm"><SelectValue placeholder="Select bounce server" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">— None —</SelectItem>
                                  {servers.filter((s) => s.type === "bounce" && s.is_active).map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Sets SMTP Return-Path and enables IMAP bounce processing.</p>
                            </div>
                            <div>
                              <Label className="text-xs font-bold uppercase tracking-wider">FBL server (optional)</Label>
                              <Select
                                value={form.config.fbl_server_id || "none"}
                                onValueChange={(v) => setForm({ ...form, config: { ...form.config, fbl_server_id: v === "none" ? "" : v } })}
                              >
                                <SelectTrigger className="mt-2 rounded-sm"><SelectValue placeholder="Select FBL server" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">— None —</SelectItem>
                                  {servers.filter((s) => s.type === "fbl" && s.is_active).map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-3">
                            <div className="label-overline mb-3">Rate limits (0 = unlimited)</div>
                            <div className="grid grid-cols-2 gap-3">
                              {RATE_FIELDS.map((f) => (
                                <div key={f.key}>
                                  <Label className="text-xs font-bold uppercase tracking-wider">{f.label}</Label>
                                  <Input
                                    type={f.type || "text"}
                                    placeholder={f.placeholder}
                                    value={form.config[f.key] || ""}
                                    onChange={(e) => setForm({ ...form, config: { ...form.config, [f.key]: e.target.value } })}
                                    className="mt-2 rounded-sm"
                                    data-testid={`server-config-${f.key}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}

                      {form.type === "bounce" && (
                        <>
                          {BOUNCE_INBOX_FIELDS.map((f) => renderInboxField(f, form, setForm))}
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            Port <strong>993</strong> + SSL, or port <strong>143</strong> + STARTTLS (typical for cPanel/hosting).
                          </p>
                        </>
                      )}

                      {form.type === "fbl" && (
                        <>
                          {FBL_INBOX_FIELDS.map((f) => renderInboxField(f, form, setForm))}
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            Feedback loop / abuse mailbox for spam complaints. Link from SMTP delivery server when ready.
                          </p>
                        </>
                      )}

                      <div className="flex items-center gap-3 pt-2">
                        <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                        <span className="text-sm">Active</span>
                      </div>
                    </div>
                    </DialogScrollBody>
                    <DialogFooter className="shrink-0 border-t border-neutral-200 dark:border-neutral-800 pt-4 flex-wrap gap-2">
                      {(form.type === "bounce" || form.type === "fbl") && (
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-sm mr-auto"
                          onClick={testInboxFromForm}
                          disabled={testingInboxForm}
                          data-testid="server-test-inbox-config"
                        >
                          {testingInboxForm ? "Testing…" : "Test connection"}
                        </Button>
                      )}
                      <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                      <Button onClick={saveServer} className="rounded-sm btn-brand font-semibold" data-testid="server-create-submit">{editingId ? "Save changes" : "Create"}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {(["delivery", "bounce", "fbl"]).map((panel) => {
                  const panelServers = servers.filter((s) =>
                    panel === "delivery" ? isDeliveryServer(s) : s.type === panel,
                  );
                  return (
                    <TabsContent key={panel} value={panel} className="mt-4">
                      {panelServers.length === 0 ? (
                        <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center">
                          <Server className="h-10 w-10 text-neutral-300 mx-auto mb-4" />
                          <h3 className="font-display font-bold text-xl mb-2">
                            {panel === "delivery" ? "No delivery servers" : panel === "bounce" ? "No bounce servers" : "No FBL servers"}
                          </h3>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">
                            {panel === "bounce"
                              ? "Add a bounce server (IMAP inbox), then select it on your SMTP delivery server."
                              : panel === "fbl"
                                ? "Add an FBL server for spam complaint processing."
                                : "Add at least one SMTP/API server for sending."}
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {panelServers.map((s) => (
                            <div key={s.id} className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm" data-testid={`server-card-${s.id}`}>
                              <div className="flex items-start justify-between mb-4">
                                <div className="h-10 w-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: "var(--bg-muted)" }}>
                                  <Server className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: s.is_active ? "#065F46" : "#92400E" }}>
                                    {s.is_active ? "Active" : "Off"}
                                  </span>
                                  <Switch checked={s.is_active} onCheckedChange={() => toggleServerActive(s)} data-testid={`server-active-toggle-${s.id}`} />
                                </div>
                              </div>
                              <h3 className="font-display font-bold text-xl tracking-tight mb-1">{s.name}</h3>
                              <div className="text-xs label-overline mb-3">{s.type}</div>
                              {(s.linked_bounce_envelope || s.bounce_envelope) && (
                                <div className="text-xs text-neutral-700 dark:text-neutral-300 mb-2 px-2 py-1.5 rounded-sm bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
                                  <span className="font-bold">{isInboxServer(s) ? "Mailbox:" : "Return-Path:"}</span>{" "}
                                  {s.linked_bounce_envelope || s.bounce_envelope}
                                </div>
                              )}
                              <ServerConfigSummary config={s.config} server={s} />
                              <div className="flex flex-wrap gap-2 pt-4 border-t border-neutral-100 dark:border-neutral-800">
                                {isDeliveryServer(s) && (
                                  <>
                                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => openMetrics(s)} data-testid={`server-metrics-${s.id}`}>
                                      <Activity className="h-3 w-3 mr-1" /> Metrics
                                    </Button>
                                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setTestServer(s); setTestForm({ to_email: "", from_email: "", from_name: "Mailwave Test" }); setTestOpen(true); }} data-testid={`server-test-${s.id}`}>
                                      <Send className="h-3 w-3 mr-1" /> Test
                                    </Button>
                                  </>
                                )}
                                {isInboxServer(s) && (
                                  <>
                                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => testImap(s)} disabled={testingImapId === s.id} title="Test IMAP connection" data-testid={`server-test-connection-${s.id}`}>
                                      {testingImapId === s.id ? "Testing…" : "Test connection"}
                                    </Button>
                                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => pollBounces(s)} disabled={pollingId === s.id} data-testid={`server-poll-${s.id}`} title={s.type === "fbl" ? "Poll FBL" : "Poll bounces"}>
                                      <Mail className="h-3 w-3" />
                                    </Button>
                                    <Button size="sm" variant="outline" className="rounded-sm text-amber-700" onClick={() => resetBouncePoll(s)} title="Reset poll cache">
                                      ↺
                                    </Button>
                                  </>
                                )}
                                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => openEdit(s)} data-testid={`server-edit-${s.id}`}>
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-sm text-red-600 hover:text-red-700" onClick={() => deleteServer(s.id)} data-testid={`server-delete-${s.id}`}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            </TabsContent>

            <TabsContent value="ssl" className="space-y-4" data-testid="admin-ssl-tab">
              <div className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm space-y-4">
                <div>
                  <p className="label-overline mb-2">Let&apos;s Encrypt</p>
                  <h3 className="font-display font-bold text-xl tracking-tight">Tracking domain SSL</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-2xl">
                    Issue origin HTTPS certificates for customer tracking hosts. DNS must point to this server.
                    If you use Cloudflare orange-cloud proxy with SSL at the edge, you may not need this — set Cloudflare SSL to Full (strict) and use a Cloudflare origin certificate instead.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
                  <Input
                    value={sslHostInput}
                    onChange={(e) => setSslHostInput(e.target.value)}
                    placeholder="track.customer.com"
                    className="rounded-sm"
                    data-testid="ssl-host-input"
                  />
                  <Button
                    onClick={() => issueSsl()}
                    disabled={!!sslIssuing}
                    className="rounded-sm btn-brand font-semibold shrink-0"
                    data-testid="ssl-issue-button"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    {sslIssuing ? "Issuing…" : "Generate SSL"}
                  </Button>
                  <Button variant="outline" onClick={loadSslDomains} disabled={sslLoading} className="rounded-sm shrink-0">
                    <RefreshCw className={`h-4 w-4 mr-2 ${sslLoading ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                </div>
              </div>

              {sslLoading && sslDomains.length === 0 ? (
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading…</div>
              ) : sslDomains.length === 0 ? (
                <div className="lift-card bg-white dark:bg-neutral-950 p-8 rounded-sm text-center text-sm text-neutral-500 dark:text-neutral-400">
                  No customer tracking domains configured yet.
                </div>
              ) : (
                <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
                  <table className="w-full" data-testid="admin-ssl-table">
                    <thead>
                      <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                        <th className="text-left px-6 py-3 label-overline">Hostname</th>
                        <th className="text-left px-6 py-3 label-overline">Users</th>
                        <th className="text-left px-6 py-3 label-overline">SSL</th>
                        <th className="text-left px-6 py-3 label-overline">Health</th>
                        <th className="text-right px-6 py-3 label-overline">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sslDomains.map((row) => (
                        <tr key={row.hostname} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                          <td className="px-6 py-4 font-mono text-sm">{row.hostname}</td>
                          <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">
                            {(row.users || []).map((u) => u.email).join(", ") || "—"}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider ${
                              row.ssl_ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
                            }`}>
                              {row.ssl_ok ? "Valid" : "Missing"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm">{row.health_ok ? "OK" : "—"}</td>
                          <td className="px-6 py-4 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-sm"
                              disabled={sslIssuing === row.hostname}
                              onClick={() => issueSsl(row.hostname)}
                            >
                              {sslIssuing === row.hostname ? "Issuing…" : "Issue SSL"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Dialog open={metricsOpen} onOpenChange={setMetricsOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{metrics?.name} — Health metrics</DialogTitle></DialogHeader>
          {metrics && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-3">
                  <div className="label-overline mb-1">Last hour</div>
                  <div className="text-2xl font-display font-black">{metrics.sent_last_hour.toLocaleString()}</div>
                  {metrics.utilization_hour_pct !== null && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{metrics.utilization_hour_pct}% of cap</div>}
                </div>
                <div className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-3">
                  <div className="label-overline mb-1">Last 24h</div>
                  <div className="text-2xl font-display font-black">{metrics.sent_last_24h.toLocaleString()}</div>
                  {metrics.utilization_day_pct !== null && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{metrics.utilization_day_pct}% of cap</div>}
                </div>
                <div className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-3">
                  <div className="label-overline mb-1">Last 7d</div>
                  <div className="text-2xl font-display font-black">{metrics.sent_last_7d.toLocaleString()}</div>
                </div>
              </div>
              <div className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-3 text-sm">
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Rate caps</div>
                <div>Hour: <strong>{metrics.max_per_hour || "unlimited"}</strong> · Day: <strong>{metrics.max_per_day || "unlimited"}</strong></div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">Assigned users: {metrics.assigned_users}</div>
                {metrics.last_campaign && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">Last campaign: {metrics.last_campaign.name} ({new Date(metrics.last_campaign.sent_at).toLocaleString()})</div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setMetricsOpen(false)} className="rounded-sm btn-brand font-semibold">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inboxTestOpen} onOpenChange={setInboxTestOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {inboxTestResult?.ok ? "Connection successful" : "Connection failed"}
            </DialogTitle>
          </DialogHeader>
          {inboxTestResult && (
            <div className="space-y-3 py-2 text-sm">
              <div className={`px-3 py-2 rounded-sm border ${inboxTestResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`}>
                {inboxTestResult.ok ? "IMAP login and folder access OK." : (inboxTestResult.error || "Could not connect")}
              </div>
              {inboxTestResult.imap_user && (
                <div><span className="font-bold text-neutral-600 dark:text-neutral-400">Login:</span> {inboxTestResult.imap_user}</div>
              )}
              {inboxTestResult.bounce_envelope && (
                <div><span className="font-bold text-neutral-600 dark:text-neutral-400">Mailbox / Return-Path:</span> {inboxTestResult.bounce_envelope}</div>
              )}
              {inboxTestResult.connection && (
                <div className="text-xs text-neutral-600 dark:text-neutral-400">
                  {inboxTestResult.connection.host}:{inboxTestResult.connection.port}
                  {" — "}
                  {(inboxTestResult.connection.security || "auto").toUpperCase()}
                </div>
              )}
              {inboxTestResult.folder != null && (
                <div><span className="font-bold text-neutral-600 dark:text-neutral-400">Folder:</span> {inboxTestResult.folder} — {inboxTestResult.folder_total ?? 0} message(s)</div>
              )}
              {inboxTestResult.folder_totals && (
                <div>
                  <span className="font-bold text-neutral-600 dark:text-neutral-400">Folder message counts:</span>
                  <ul className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 list-disc pl-4 max-h-32 overflow-y-auto">
                    {Object.entries(inboxTestResult.folder_totals)
                      .filter(([, n]) => n >= 0)
                      .map(([name, count]) => (
                        <li key={name}>{name}: {count}</li>
                      ))}
                  </ul>
                </div>
              )}
              {inboxTestResult.folder_hint && (
                <div className="px-3 py-2 rounded-sm border bg-amber-50 border-amber-200 text-amber-900 text-xs">
                  {inboxTestResult.folder_hint}
                </div>
              )}
              {inboxTestResult.search && (
                <div>
                  <span className="font-bold text-neutral-600 dark:text-neutral-400">Search:</span>{" "}
                  {Object.entries(inboxTestResult.search).map(([k, v]) => `${k}=${v}`).join(", ")}
                </div>
              )}
              {inboxTestResult.sample_parsed_recipient && (
                <div><span className="font-bold text-neutral-600 dark:text-neutral-400">Sample parsed bounce:</span> {inboxTestResult.sample_parsed_recipient}</div>
              )}
              {inboxTestResult.sample_parse_ok === false && (
                <div className="px-3 py-2 rounded-sm border bg-amber-50 border-amber-200 text-amber-900 text-xs">
                  Latest message in folder could not be parsed as a bounce
                  {inboxTestResult.sample_subject ? ` (“${inboxTestResult.sample_subject}”)` : ""}.
                  {inboxTestResult.sample_body_excerpt && (
                    <div className="mt-2 text-neutral-700 dark:text-neutral-300 break-words">
                      Preview: {inboxTestResult.sample_body_excerpt}
                    </div>
                  )}
                  Check IMAP folder or contact support if format is non-standard.
                </div>
              )}
              {inboxTestResult.sample_subjects?.length > 0 && (
                <div>
                  <span className="font-bold text-neutral-600 dark:text-neutral-400">Latest subject:</span> {inboxTestResult.sample_subjects[0]}
                </div>
              )}
              {inboxTestResult.mailboxes?.length > 0 && (
                <div>
                  <span className="font-bold text-neutral-600 dark:text-neutral-400">Available folders:</span>
                  <ul className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 list-disc pl-4 max-h-32 overflow-y-auto">
                    {(inboxTestResult.mailboxes || []).slice(0, 12).map((mb, i) => (
                      <li key={i}>{mb}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setInboxTestOpen(false)} className="rounded-sm btn-brand font-semibold">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send test email via {testServer?.name}</DialogTitle></DialogHeader>
          <form
            className="space-y-4 py-2"
            onSubmit={(e) => {
              e.preventDefault();
              sendTest();
            }}
          >
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">To</Label>
              <Input type="email" required value={testForm.to_email} onChange={(e) => setTestForm({ ...testForm, to_email: e.target.value })} className="mt-2 rounded-sm" data-testid="test-to-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">From email</Label>
              <Input type="email" required value={testForm.from_email} onChange={(e) => setTestForm({ ...testForm, from_email: e.target.value })} className="mt-2 rounded-sm" data-testid="test-from-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">From name</Label>
              <Input value={testForm.from_name} onChange={(e) => setTestForm({ ...testForm, from_name: e.target.value })} className="mt-2 rounded-sm" data-testid="test-from-name-input" />
            </div>
            <DialogFooter className="px-0 pb-0">
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => setTestOpen(false)} disabled={testingSend}>Cancel</Button>
              <Button type="submit" className="rounded-sm btn-brand font-semibold" disabled={testingSend} data-testid="test-send-button">
                {testingSend ? "Sending…" : "Send test"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={userCreateOpen} onOpenChange={setUserCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Name</Label>
                <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} className="mt-2 rounded-sm" data-testid="new-user-name" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Company</Label>
                <Input value={userForm.company} onChange={(e) => setUserForm({ ...userForm, company: e.target.value })} className="mt-2 rounded-sm" data-testid="new-user-company" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
              <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} className="mt-2 rounded-sm" data-testid="new-user-email" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Password</Label>
              <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} className="mt-2 rounded-sm" data-testid="new-user-password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Plan</Label>
                <Select value={userForm.plan} onValueChange={(v) => setUserForm({ ...userForm, plan: v })}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="new-user-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Email credits</Label>
                <Input type="number" placeholder="Blank = unlimited" value={userForm.email_credits} onChange={(e) => setUserForm({ ...userForm, email_credits: e.target.value })} className="mt-2 rounded-sm" data-testid="new-user-credits" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Organization</Label>
              <Select value={userForm.organization_id || "__none__"} onValueChange={(v) => setUserForm({ ...userForm, organization_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="mt-2 rounded-sm" data-testid="new-user-org"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Switch checked={userForm.is_admin} onCheckedChange={(v) => setUserForm({ ...userForm, is_admin: v })} data-testid="new-user-admin" />
              <span className="text-sm">Grant admin access</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserCreateOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={createUser} className="rounded-sm btn-brand font-semibold" data-testid="new-user-submit">Create user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Manage {manageUser?.name}</DialogTitle></DialogHeader>
          {manageUser && (
            <div className="space-y-6 py-2 max-h-[70vh] overflow-y-auto pr-1">
              <div className="space-y-3">
                <div className="label-overline">Profile</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Name</Label>
                    <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-2 rounded-sm" data-testid="manage-name" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Company</Label>
                    <Input value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} className="mt-2 rounded-sm" data-testid="manage-company" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
                  <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-2 rounded-sm" data-testid="manage-email" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Organization</Label>
                  <Select value={editForm.organization_id || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, organization_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="manage-org"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Members share team sending domains added with &quot;Share with team&quot;.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={editForm.is_admin} onCheckedChange={(v) => setEditForm({ ...editForm, is_admin: v })} data-testid="manage-admin" />
                  <span className="text-sm">Admin access</span>
                </div>
                <Button onClick={saveUser} variant="outline" className="rounded-sm font-semibold" data-testid="manage-save">Save profile</Button>
              </div>

              <div className="space-y-3 border-t border-neutral-200 dark:border-neutral-800 pt-5">
                <div className="label-overline">Plan — currently <strong>{manageUser.plan || "free"}</strong>{manageUser.plan_until ? ` (until ${new Date(manageUser.plan_until).toLocaleDateString()})` : ""}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Plan</Label>
                    <Select value={planForm.plan} onValueChange={(v) => setPlanForm({ ...planForm, plan: v })}>
                      <SelectTrigger className="mt-2 rounded-sm" data-testid="manage-plan-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Duration (days)</Label>
                    <Input type="number" value={planForm.days} onChange={(e) => setPlanForm({ ...planForm, days: e.target.value })} className="mt-2 rounded-sm" disabled={planForm.plan === "free"} data-testid="manage-plan-days" />
                  </div>
                </div>
                <Button onClick={allocatePlan} variant="outline" className="rounded-sm font-semibold" data-testid="manage-plan-submit"><CreditCard className="h-3.5 w-3.5 mr-2" /> Allocate plan</Button>
              </div>

              <div className="space-y-3 border-t border-neutral-200 dark:border-neutral-800 pt-5">
                <div className="label-overline">Email credits — currently <strong>{creditLabel(manageUser.email_credits)}</strong></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Mode</Label>
                    <Select value={creditForm.mode} onValueChange={(v) => setCreditForm({ ...creditForm, mode: v })}>
                      <SelectTrigger className="mt-2 rounded-sm" data-testid="manage-credit-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="set">Set to</SelectItem>
                        <SelectItem value="add">Add</SelectItem>
                        <SelectItem value="unlimited">Unlimited</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Amount</Label>
                    <Input type="number" value={creditForm.amount} onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })} className="mt-2 rounded-sm" disabled={creditForm.mode === "unlimited"} data-testid="manage-credit-amount" />
                  </div>
                </div>
                <Button onClick={allocateCredits} variant="outline" className="rounded-sm font-semibold" data-testid="manage-credit-submit"><Mail className="h-3.5 w-3.5 mr-2" /> Apply credits</Button>
              </div>

              <div className="space-y-3 border-t border-neutral-200 dark:border-neutral-800 pt-5">
                <div className="label-overline">
                  Delivery server — currently{" "}
                  <strong>
                    {manageUser.delivery_server_locked
                      ? `locked to ${manageUser.delivery_server_name || "unknown"}`
                      : manageUser.delivery_server_name || "auto rotation"}
                  </strong>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Lock a customer to one SMTP server so their campaigns always send through it instead of rotating.
                </p>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">SMTP server</Label>
                  <Select
                    value={serverForm.server_id || "__auto__"}
                    onValueChange={(v) => setServerForm({ ...serverForm, server_id: v === "__auto__" ? "" : v })}
                  >
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="manage-server-select">
                      <SelectValue placeholder="Auto (rotate servers)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__auto__">Auto — rotate active servers</SelectItem>
                      {sendServers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} ({s.type}){!s.is_active ? " — inactive" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={serverForm.locked}
                    onCheckedChange={(v) => setServerForm({ ...serverForm, locked: v })}
                    data-testid="manage-server-lock"
                  />
                  <span className="text-sm">Lock to this server (disable rotation)</span>
                </div>
                <Button
                  onClick={saveServerAssignment}
                  variant="outline"
                  disabled={savingServer}
                  className="rounded-sm font-semibold"
                  data-testid="manage-server-submit"
                >
                  <Server className="h-3.5 w-3.5 mr-2" />
                  {savingServer ? "Saving…" : "Apply server assignment"}
                </Button>
              </div>

              <div className="space-y-3 border-t border-neutral-200 dark:border-neutral-800 pt-5">
                <div className="label-overline">Account status</div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{manageUser.suspended ? "This account is suspended and cannot log in." : "Account is active."}</span>
                  <Button onClick={() => toggleSuspend(manageUser)} variant="outline" className={`rounded-sm font-semibold ${manageUser.suspended ? "text-emerald-700" : "text-red-600"}`} data-testid="manage-suspend">
                    {manageUser.suspended ? <><UserCheck className="h-3.5 w-3.5 mr-2" /> Reactivate</> : <><UserX className="h-3.5 w-3.5 mr-2" /> Suspend</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setManageOpen(false)} className="rounded-sm btn-brand font-semibold">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={planEditOpen} onOpenChange={(open) => { setPlanEditOpen(open); if (!open) resetPlanForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{planEditingId ? "Edit plan" : "Create plan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!planEditingId && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Plan id</Label>
                <Input
                  value={planEditorForm.id}
                  onChange={(e) => setPlanEditorForm({ ...planEditorForm, id: e.target.value })}
                  className="mt-2 rounded-sm font-mono text-sm"
                  placeholder="starter"
                  data-testid="plan-id-input"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Lowercase slug — used internally and in billing.</p>
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Name</Label>
              <Input
                value={planEditorForm.name}
                onChange={(e) => setPlanEditorForm({ ...planEditorForm, name: e.target.value })}
                className="mt-2 rounded-sm"
                data-testid="plan-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Price (USD)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={planEditorForm.price}
                  onChange={(e) => setPlanEditorForm({ ...planEditorForm, price: e.target.value })}
                  className="mt-2 rounded-sm"
                  data-testid="plan-price-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Sort order</Label>
                <Input
                  type="number"
                  value={planEditorForm.sort_order}
                  onChange={(e) => setPlanEditorForm({ ...planEditorForm, sort_order: e.target.value })}
                  className="mt-2 rounded-sm"
                  data-testid="plan-sort-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Max contacts</Label>
                <Input
                  type="number"
                  min="0"
                  value={planEditorForm.max_contacts}
                  onChange={(e) => setPlanEditorForm({ ...planEditorForm, max_contacts: e.target.value })}
                  className="mt-2 rounded-sm"
                  data-testid="plan-contacts-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Max emails / month</Label>
                <Input
                  type="number"
                  min="0"
                  value={planEditorForm.max_emails_per_month}
                  onChange={(e) => setPlanEditorForm({ ...planEditorForm, max_emails_per_month: e.target.value })}
                  className="mt-2 rounded-sm"
                  data-testid="plan-emails-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Features (one per line)</Label>
              <Textarea
                rows={4}
                value={planEditorForm.features}
                onChange={(e) => setPlanEditorForm({ ...planEditorForm, features: e.target.value })}
                className="mt-2 rounded-sm text-sm"
                placeholder="Unlimited audiences&#10;A/B testing"
                data-testid="plan-features-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={planEditorForm.is_active}
                onCheckedChange={(v) => setPlanEditorForm({ ...planEditorForm, is_active: v })}
                disabled={planEditingId === "free"}
                data-testid="plan-active-switch"
              />
              <span className="text-sm">Visible on billing page</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanEditOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={savePlan} disabled={savingPlan} className="rounded-sm btn-brand font-semibold" data-testid="plan-save-button">
              {savingPlan ? "Saving…" : planEditingId ? "Save plan" : "Create plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={orgMembersOpen} onOpenChange={setOrgMembersOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Members — {orgMembersTarget?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Select which user accounts belong to this organization. All selected users can send from shared team domains.
          </p>
          <DialogScrollBody className="max-h-[50vh]">
            <div className="space-y-2 py-2">
              {allUsersForOrg.map((u) => {
                const checked = orgMemberIds.includes(u.id);
                return (
                  <label key={u.id} className="flex items-center gap-3 p-3 rounded-sm border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 cursor-pointer">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        setOrgMemberIds((prev) => (v ? [...prev, u.id] : prev.filter((id) => id !== u.id)));
                      }}
                      data-testid={`org-member-${u.id}`}
                    />
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{u.name}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{u.email}</div>
                    </div>
                  </label>
                );
              })}
              {!allUsersForOrg.length && (
                <div className="text-sm text-neutral-500 dark:text-neutral-400 py-4 text-center">No users loaded.</div>
              )}
            </div>
          </DialogScrollBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrgMembersOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveOrgMembers} disabled={savingOrgMembers} className="rounded-sm btn-brand font-semibold" data-testid="org-members-save">
              {savingOrgMembers ? "Saving…" : `Save (${orgMemberIds.length} selected)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendFailuresOpen} onOpenChange={setSendFailuresOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Send failures — {sendFailuresCampaign?.name}</DialogTitle>
          </DialogHeader>
          <DialogScrollBody className="max-h-[60vh]">
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
              Each row is a recipient the delivery server rejected or could not accept. New sends record failures automatically.
            </p>
            {sendFailuresLoading ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 py-8 text-center">Loading…</p>
            ) : sendFailuresTotal === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 py-8 text-center">
                No per-email failure log for this campaign yet. Failures are recorded on sends after this update was deployed.
              </p>
            ) : (
              <div className="rounded-sm border border-neutral-200 dark:border-neutral-800 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
                      <th className="text-left px-4 py-2 label-overline">Email</th>
                      <th className="text-left px-4 py-2 label-overline">Reason</th>
                      <th className="text-left px-4 py-2 label-overline w-24">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sendFailures.map((row) => (
                      <tr key={`${row.contact_id}-${row.at}`} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 align-top">
                        <td className="px-4 py-3 font-mono text-xs break-all">{row.email || "—"}</td>
                        <td className="px-4 py-3 text-xs text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap break-words max-w-md">
                          {row.variant ? <span className="text-neutral-400 block mb-1">Variant {row.variant}</span> : null}
                          {row.reason || "Send failed"}
                        </td>
                        <td className="px-4 py-3 text-xs text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
                          {row.at ? new Date(row.at).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <ListPagination
                  page={sendFailuresPage}
                  pages={sendFailuresPages}
                  total={sendFailuresTotal}
                  limit={sendFailuresLimit}
                  onPageChange={(p) => sendFailuresCampaign && loadSendFailures(sendFailuresCampaign.id, p, sendFailuresLimit)}
                  onLimitChange={(lim) => {
                    setSendFailuresLimit(lim);
                    if (sendFailuresCampaign) loadSendFailures(sendFailuresCampaign.id, 1, lim);
                  }}
                />
              </div>
            )}
          </DialogScrollBody>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSendFailuresOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
